import { EnvelopeIcon } from '@heroicons/react/24/outline';
import type { EmailProviderConfig, EmailProvider } from './EmailSettingsForm';
import { EMAIL_PROVIDERS } from './EmailSettingsForm';

interface EmailProviderFieldsProps {
  config: EmailProviderConfig;
  setConfig: React.Dispatch<React.SetStateAction<EmailProviderConfig>>;
  errors: Record<string, string>;
}

export default function EmailProviderFields({ config, setConfig, errors }: EmailProviderFieldsProps) {
  const provider = EMAIL_PROVIDERS.find(p => p.id === config.provider);
  const providerName = provider?.name || config.provider;

  return (
    <div>
      <h3 className="text-lg font-medium text-golf-gray mb-4 flex items-center gap-2">
        <EnvelopeIcon className="w-5 h-5 text-golf-orange" />
        {providerName} Configuration
      </h3>

      {config.provider === 'sendgrid' && (
        <SendGridFields config={config} setConfig={setConfig} errors={errors} />
      )}
      {config.provider === 'mailgun' && (
        <MailgunFields config={config} setConfig={setConfig} errors={errors} />
      )}
      {config.provider === 'postmark' && (
        <PostmarkFields config={config} setConfig={setConfig} errors={errors} />
      )}
      {config.provider === 'resend' && (
        <ResendFields config={config} setConfig={setConfig} errors={errors} />
      )}
      {config.provider === 'smtp' && (
        <SmtpFields config={config} setConfig={setConfig} errors={errors} />
      )}
    </div>
  );
}

type FieldProps = {
  config: EmailProviderConfig;
  setConfig: React.Dispatch<React.SetStateAction<EmailProviderConfig>>;
  errors: Record<string, string>;
};

function SendGridFields({ config, setConfig, errors }: FieldProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
        <input
          type="password"
          value={config.sendgrid?.api_key || ''}
          onChange={e => setConfig(prev => ({
            ...prev,
            sendgrid: { ...prev.sendgrid!, api_key: e.target.value },
          }))}
          className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange ${errors.sendgrid_api_key ? 'border-red-300' : ''}`}
          placeholder="SG.xxxxxxxxxx"
        />
        {errors.sendgrid_api_key && <p className="mt-1 text-sm text-red-600">{errors.sendgrid_api_key}</p>}
        <p className="mt-1 text-xs text-gray-500">
          Find your API key at dashboard.sendgrid.com under Settings {'>'} API Keys
        </p>
      </div>
    </div>
  );
}

function MailgunFields({ config, setConfig, errors }: FieldProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
          <input
            type="password"
            value={config.mailgun?.api_key || ''}
            onChange={e => setConfig(prev => ({
              ...prev,
              mailgun: { ...prev.mailgun!, api_key: e.target.value },
            }))}
            className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange ${errors.mailgun_api_key ? 'border-red-300' : ''}`}
            placeholder="key-xxxxxxxxxx"
          />
          {errors.mailgun_api_key && <p className="mt-1 text-sm text-red-600">{errors.mailgun_api_key}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Domain</label>
          <input
            type="text"
            value={config.mailgun?.domain || ''}
            onChange={e => setConfig(prev => ({
              ...prev,
              mailgun: { ...prev.mailgun!, domain: e.target.value },
            }))}
            className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange ${errors.mailgun_domain ? 'border-red-300' : ''}`}
            placeholder="mg.example.com"
          />
          {errors.mailgun_domain && <p className="mt-1 text-sm text-red-600">{errors.mailgun_domain}</p>}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
        <select
          value={config.mailgun?.region || 'us'}
          onChange={e => setConfig(prev => ({
            ...prev,
            mailgun: { ...prev.mailgun!, region: e.target.value as 'us' | 'eu' },
          }))}
          className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
        >
          <option value="us">United States</option>
          <option value="eu">European Union</option>
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Select the region where your Mailgun account is hosted
        </p>
      </div>
    </div>
  );
}

function PostmarkFields({ config, setConfig, errors }: FieldProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Server Token</label>
        <input
          type="password"
          value={config.postmark?.server_token || ''}
          onChange={e => setConfig(prev => ({
            ...prev,
            postmark: { ...prev.postmark!, server_token: e.target.value },
          }))}
          className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange ${errors.postmark_server_token ? 'border-red-300' : ''}`}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
        />
        {errors.postmark_server_token && <p className="mt-1 text-sm text-red-600">{errors.postmark_server_token}</p>}
        <p className="mt-1 text-xs text-gray-500">
          Find your server token in Postmark under Servers {'>'} Your Server {'>'} API Tokens
        </p>
      </div>
    </div>
  );
}

function ResendFields({ config, setConfig, errors }: FieldProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
        <input
          type="password"
          value={config.resend?.api_key || ''}
          onChange={e => setConfig(prev => ({
            ...prev,
            resend: { ...prev.resend!, api_key: e.target.value },
          }))}
          className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange ${errors.resend_api_key ? 'border-red-300' : ''}`}
          placeholder="re_xxxxxxxxxx"
        />
        {errors.resend_api_key && <p className="mt-1 text-sm text-red-600">{errors.resend_api_key}</p>}
        <p className="mt-1 text-xs text-gray-500">
          Find your API key at resend.com/api-keys
        </p>
      </div>
    </div>
  );
}

function SmtpFields({ config, setConfig, errors }: FieldProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Host</label>
          <input
            type="text"
            value={config.smtp?.host || ''}
            onChange={e => setConfig(prev => ({
              ...prev,
              smtp: { ...prev.smtp!, host: e.target.value },
            }))}
            className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange ${errors.smtp_host ? 'border-red-300' : ''}`}
            placeholder="smtp.example.com"
          />
          {errors.smtp_host && <p className="mt-1 text-sm text-red-600">{errors.smtp_host}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
          <input
            type="number"
            value={config.smtp?.port || 587}
            onChange={e => setConfig(prev => ({
              ...prev,
              smtp: { ...prev.smtp!, port: parseInt(e.target.value) || 587 },
            }))}
            className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange ${errors.smtp_port ? 'border-red-300' : ''}`}
            placeholder="587"
          />
          {errors.smtp_port && <p className="mt-1 text-sm text-red-600">{errors.smtp_port}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
          <input
            type="text"
            value={config.smtp?.username || ''}
            onChange={e => setConfig(prev => ({
              ...prev,
              smtp: { ...prev.smtp!, username: e.target.value },
            }))}
            className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange ${errors.smtp_username ? 'border-red-300' : ''}`}
            placeholder="user@example.com"
          />
          {errors.smtp_username && <p className="mt-1 text-sm text-red-600">{errors.smtp_username}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <input
            type="password"
            value={config.smtp?.password || ''}
            onChange={e => setConfig(prev => ({
              ...prev,
              smtp: { ...prev.smtp!, password: e.target.value },
            }))}
            className={`block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange ${errors.smtp_password ? 'border-red-300' : ''}`}
          />
          {errors.smtp_password && <p className="mt-1 text-sm text-red-600">{errors.smtp_password}</p>}
        </div>
      </div>
      <div className="flex items-center mt-2">
        <input
          type="checkbox"
          id="smtp_secure"
          checked={config.smtp?.secure ?? true}
          onChange={e => setConfig(prev => ({
            ...prev,
            smtp: { ...prev.smtp!, secure: e.target.checked },
          }))}
          className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
        />
        <label htmlFor="smtp_secure" className="ml-2 block text-sm text-gray-700">
          Use secure connection (TLS/SSL)
        </label>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <p className="text-sm text-amber-800">
          For best reliability, consider using an API-based provider (SendGrid, Mailgun, Postmark, or Resend) instead of direct SMTP.
        </p>
      </div>
    </div>
  );
}
